import React from 'react';
import Chatbot, { openAssistant } from './Chat';

const services = [
  {
    title: 'Workflow Automation',
    text: 'Clarify where manual work is costing time or revenue, then automate intake, follow-up, handoffs, reporting, and routing with measurable success criteria.'
  },
  {
    title: 'Operations Agents',
    text: 'Create agents that triage requests, draft responses, update systems, route work, and keep business processes moving with clear ownership and review paths.'
  },
  {
    title: 'Knowledge Assistants',
    text: 'Give staff and customers accurate answers from approved documents, policies, products, procedures, and business data so decisions are faster and more consistent.'
  },
  {
    title: 'Voice & Chat AI',
    text: 'Launch customer-facing assistants for phone, chat, scheduling, support, routing, and after-hours coverage with practical guardrails around risk.'
  }
];

const outcomes = [
  'Aligned priorities: separate attractive AI ideas from the workflow most likely to improve speed, quality, or revenue protection.',
  'Clearer decisions: translate feasibility, data access, privacy, and operational risk into concrete recommendations executives and operators can act on.',
  'Unblocked delivery: identify dependency owners, next steps, and launch criteria so the project moves from discussion to production use.'
];

const experience = [
  'AI Engineer, embedded two years with a Fortune 100 entertainment and media company. Built ingestion, search, and retrieval systems for guest-facing services, a destination travel planner, and call center tools used by 10,000 representatives.',
  'Founding engineer of Sayable.ai, an AI phone receptionist combining speech recognition, LLMs, retrieval, telephony, text-to-speech, and business workflows.',
  'Software Engineer at AWS. Shipped a serverless media processing platform on Lambda, API Gateway, SQS, and SNS, and built GDPR compliance tooling for AWS resources.',
  'Senior Software Engineer and Team Lead at Skidmore, Owings & Merrill. Led a team building internal applications, APIs, data systems, and ETL pipelines for architects, designers, and operations teams.'
];

const process = [
  ['01', 'Align on the right opportunity', 'We clarify the workflow, stakeholders, business value, and constraints so the first AI project is worth building.'],
  ['02', 'Prove it with a focused pilot', 'You get a working system connected to your data and tools, with clear success criteria and review points.'],
  ['03', 'Turn it into operating software', 'The final system includes permissions, monitoring, evaluation, documentation, ownership, and a practical handoff.']
];

function Home() {
  return (
    <main>
      <section className="hero" id="top">
        <div className="hero-content-wrap">
          <p className="eyebrow">Applied AI consulting for small and mid-sized businesses</p>
          <h1>AI automation for the workflows costing your business time.</h1>
          <p className="hero-lede">
            Chase AI helps clients turn AI opportunities into practical, lower-risk delivery
            plans, then builds agents, knowledge assistants, and customer-facing tools that
            connect to real data and fit the way teams already work.
          </p>
          <div className="hero-actions">
            <button className="primary-action" type="button" onClick={openAssistant}>
              Start a project
            </button>
            <a className="secondary-action" href="#services">
              See services
            </a>
          </div>
        </div>
      </section>

      <section className="intro-band" id="services">
        <div className="section-shell two-column">
          <div>
            <p className="section-kicker">What I Build</p>
            <h2>Systems your team can use, not AI experiments that stall.</h2>
          </div>
          <p className="section-copy">
            Impactful AI consulting for SMBs starts with a specific business workflow: support
            requests, lead follow-up, document search, scheduling, reporting, or internal operations.
            I clarify feasibility, dependencies, risk, and business value up front, then design
            and build the software so the result is measurable, maintainable, and useful after
            the first demo.
          </p>
        </div>
      </section>

      <section className="services-grid section-shell" aria-label="AI consulting services">
        {services.map((service) => (
          <article className="service-card" key={service.title}>
            <h3>{service.title}</h3>
            <p>{service.text}</p>
          </article>
        ))}
      </section>

      <section className="outcomes-band">
        <div className="section-shell">
          <p className="section-kicker">How I Help Clients Decide</p>
          <h2>Technical execution is stronger when the business case, constraints, and next steps are clear.</h2>
          <div className="outcome-list">
            {outcomes.map((outcome) => (
              <div key={outcome}>{outcome}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell profile-section" id="about">
        <div className="profile-image-wrap">
          <img src="/dale-chase-15.jpg" alt="Dale Chase" />
        </div>
        <div className="profile-copy">
          <p className="section-kicker">Why Chase AI</p>
          <h2>A senior AI engineer who can take the project from idea to production.</h2>
          <p>
            I am Dale Chase, an AI engineer and senior software engineer based in Oakland, CA.
            I build the full system: product workflow, backend services, retrieval, model
            integration, data pipelines, cloud deployment, dashboards, and production support.
          </p>
          <p>
            For small and mid-sized businesses, that means you are not hiring a strategist who
            stops at recommendations or an implementer who ignores the business case. You are
            hiring an engineer who can align stakeholders, scope the right pilot, ship it,
            connect it to your tools, and make it reliable enough to use.
          </p>
          <div className="profile-links">
            <a href="https://www.linkedin.com/in/dale-chase/" target="_blank" rel="noreferrer">
              LinkedIn
            </a>
            <a href="mailto:dale@chase-ai.com">dale@chase-ai.com</a>
          </div>
        </div>
      </section>

      <section className="proof-band">
        <div className="section-shell">
          <p className="section-kicker">Experience</p>
          <h2>Fifteen years shipping production systems, now focused on applied AI.</h2>
          <div className="proof-grid">
            {experience.map((item) => (
              <div className="proof-item" key={item}>{item}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell process-section" id="process">
        <div className="process-heading">
          <p className="section-kicker">Engagement Model</p>
          <h2>Start with the decision that matters. Build only after the path is clear.</h2>
        </div>
        <div className="process-steps">
          {process.map(([number, title, text]) => (
            <article className="process-step" key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="cta-section" id="contact">
        <div className="section-shell cta-shell">
          <img src="/cai-logo.png" alt="" aria-hidden="true" />
          <div>
            <p className="section-kicker">Work With Me</p>
            <h2>Have a workflow that should not still be manual?</h2>
            <p>
              Send me the bottleneck. I will help you decide whether AI is the right tool,
              what a first version should do, which risks or dependencies matter, and what it
              would take to make it production-ready.
            </p>
          </div>
          <button className="primary-action" type="button" onClick={openAssistant}>
            Contact Dale
          </button>
        </div>
      </section>

      <Chatbot />
    </main>
  );
}

export default Home;
